
import Vue, { PropType } from 'vue';
import { LogoLibraryHeader } from './Header';
import { ActionBar } from './ActionBar';
import { ImageThumbnails } from './ImageThumbnails';
import { ErrorNotification } from '../../libs/proof-approval/ErrorNotification';
import {
  CvGrid,
  CvColumn,
  CvRow,
  CvInlineLoading,
  CvLoading,
} from '@carbon/vue';
import QuestionAboutWhite from '@/components/QuestionAboutWhiteColor/QuestionAboutWhite.vue';
import QuestionAboutWhiteReplace from '@/components/QuestionAboutWhiteColor/QuestionAboutWhiteReplace.vue';
import { WizardPayload, PaggedCollection } from './typings';
import customersAPI from '@/services/CustomersService';
import imageProcessingAPI from '@/services/FileService';
import { ItemActions } from './ItemActions';
import { UploadArtworkDialog } from './UploadArtworkDialog';
import { EditArtworkDialog } from './EditArtworkDialog';
import { throttle } from 'throttle-debounce';
import axios, { AxiosError } from 'axios';
import { base64StringToBlob } from 'blob-util';
import { SearchByCriteria } from './typings';
import { v4 as uuidv4 } from 'uuid';
import { SupportedLangCodes } from '../../constants/languageCodes';
import {
  ImageProcessingEngineVersion,
  DoSmallDetailsPart,
} from '../../constants/imageProcessingEngine';
import {
  Artwork,
  ArtworkResult,
  VFileUploaderFile,
} from '@/components/w2pFileUploader';
import { mapState } from 'pinia';
import { SetArtworkBackgroundCommand } from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import {
  SmallDetailsLoader,
  SmallDetailsOneLogo,
} from '@/components/SmallDetails';
import { lineWidth, lineWidthV2 } from '@/constants/areaLineWidth';
import { px2mm } from '@/utils';
import { SmallDetailsResult } from '@/typings';
import {
  CheckImageSmallDetailsCommand,
  NewLogoImprovementRequestForm,
  SmallDetailsPayload,
} from '@/typings/SmallDetails';
import { SmallDetailsLimitations } from '@midocean/webapps-shared-domain';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useProductStoreV2 } from '@/stores/v2/product';
import { useStore as useLangStore } from '@/stores/lang';
import { useStore as useLangStoreV2 } from '@/stores/v2/lang';
import SmallDetailsSingleLogoModal from '../import-wizard-v2/components/SmallDetails/SmallDetailsSingleLogo/SmallDetailsSingleLogoModal.vue';
import { LOGO_IMPROVEMENT_FLOW_ACTIVE } from '@/constants/smallDetails';
import LogoImprovementRequestSuccessModal from '../import-wizard-v2/components/SmallDetails/LogoImprovementRequestSuccessModal/LogoImprovementRequestSuccessModal.vue';
import {
  BACKGROUND_IMAGE_DPI,
  BACKGROUND_IMAGE_W,
} from '../import-wizard-v2/constants';
import { fileTypeValidatorSmallDetails } from '../import-wizard-v2/validators';

export default Vue.extend({
  components: {
    CvGrid,
    CvRow,
    CvColumn,
    CvInlineLoading,
    CvLoading,
    ItemActions,
    LogoLibraryHeader,
    ActionBar,
    ImageThumbnails,
    UploadArtworkDialog,
    EditArtworkDialog,
    SmallDetailsLoader,
    SmallDetailsOneLogo,
    ErrorNotification,
    QuestionAboutWhite,
    QuestionAboutWhiteReplace,
    SmallDetailsSingleLogoModal,
    LogoImprovementRequestSuccessModal,
  },
  props: {
    sapCustomerId: {
      type: String,
      required: true,
    },
    env: {
      type: Object,
      default: () => ({ context: 'myAccount' }),
    },
    lang: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'en',
    },
    useVersion2: {
      type: Boolean,
      required: false,
      default: false,
    },
    sapCustomerEmail: {
      type: String,
      required: false,
      default: '',
    },
    region: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      artworksCollection: {} as PaggedCollection,
      smallDetailsResult: null as SmallDetailsResult | null,
      wizardPayload: null as WizardPayload | null,
      limitation: {} as SmallDetailsLimitations | undefined,
      thumbEl: null as unknown as any,
      customerId: '',
      isLoading: false,
      showSmallDetailsLoader: false,
      isSmallDetailsOpen: false,
      isDeleting: false,
      activeDialog: '',
      selectedArtworkId: '',
      searchTerm: '',
      page: 1,
      pageSize: 12,
      tagOptions: [] as string[],
      selectedTags: [] as string[],
      uuidv4,
      isNotificationVisible: false,
      errorMessage: '',
      LOGO_IMPROVEMENT_FLOW_ACTIVE,
      isCreatingLogoImprovementRequest: false,
      fileTypeValid: true,
    };
  },

  computed: {
    ...mapState(useProductStore, ['isSublimationTechnique']),
    ...mapState(useProductStoreV2, [
      'isSublimationTechnique',
      'data',
      'activePosition',
      'unavailablePrintPositions',
    ]),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useLangStoreV2, ['languageInUse']),
    // Need to throtlle function because of scroll event calling the Customer API multiple times for every incremented page
    throttledOnScroll(): any {
      return throttle(200, this.onScroll);
    },
    searchByCriteria(): SearchByCriteria {
      return { title: this.searchTerm, tags: this.selectedTags };
    },
  },

  watch: {
    async languageInUse() {
      if (process.env.VUE_APP_USE_LOCAL_TRANSLATIONS === 'false') {
        var translationsJson = await customersAPI.getTranslations(
          this.languageInUse
        );
        this.$i18n.setLocaleMessage(this.languageInUse, translationsJson.data);
      }
    },
    async searchTerm() {
      this.artworksCollection = {} as PaggedCollection;
      await this.loadArtworks({
        customerId: this.sapCustomerId,
        searchBy: this.searchByCriteria,
        page: 1,
        pageSize: this.pageSize,
      });

      // Deselect artwork if a search term was entered in the search bar
      this.selectedArtworkId = '';
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async page(current, _old) {
      // This check is necessary for now, because in the doSearch method we change the values of both searchTerm and page which both invoke there respective watcher functions and thus the Customer API is called twice
      // So if the page Data property has been manually set in the doSearch method to the value of 1, the call to the Customers API will be skipped in the page watcher function
      // This is okay because every page number larger than 1 invokes the watcher function (so onScroll works without a problem)
      if (current !== 1) {
        await this.loadArtworks({
          customerId: this.sapCustomerId,
          searchBy: this.searchByCriteria,
          page: current,
          pageSize: this.pageSize,
        });
      }
    },
  },

  async mounted() {
    await this.loadArtworks({
      customerId: this.sapCustomerId,
      searchBy: this.searchByCriteria,
      page: this.page,
      pageSize: this.pageSize,
    });

    this.populateCustomerId();

    this.tagOptions = await this.loadTags(this.customerId);

    this.thumbEl = this.$refs['thumbnail-list'];
    if (this.thumbEl) {
      this.thumbEl.$el.addEventListener('scroll', this.throttledOnScroll);

      this.$on('hook:beforeDestroy', () => {
        this.thumbEl.$el.removeEventListener('scroll', this.throttledOnScroll);
      });
    }
  },

  methods: {
    async keepWhite() {
      if (this.isSublimationTechnique) {
        this.$modal.hide('question-about-white-replace');
      } else {
        this.$modal.hide('question-about-white');
      }

      await this.finalizePayloadProcessing();
    },
    async removeWhite() {
      if (this.isSublimationTechnique) {
        this.$modal.hide('question-about-white-replace');
      } else {
        this.$modal.hide('question-about-white');
      }

      const command: SetArtworkBackgroundCommand = {
        isFullBackground: true,
        color: 2,
      };
      const { data } = await imageProcessingAPI.changeLogoBackground(
        this.wizardPayload?.pngFile || '',
        command
      );

      if (this.wizardPayload) {
        this.wizardPayload.pngFile = base64StringToBlob(
          data.pngFile,
          'image/png'
        );
        this.wizardPayload.pngFileWithoutConverters = base64StringToBlob(
          data.pngFile,
          'image/png'
        );
        this.wizardPayload.pngHeightPixels = data.heightInPx;
        this.wizardPayload.pngWidthPixels = data.widthInPx;

        if (data.isAllWhiteRemoved) {
          const whiteColorIndex =
            this.wizardPayload.pmsColorsInPngFile.findIndex(
              (c) => c.name === 'Pure White'
            );
          if (whiteColorIndex !== -1) {
            this.wizardPayload.pmsColorsInPngFile.splice(whiteColorIndex, 1);
          }
        }
      }

      await this.finalizePayloadProcessing();
    },
    async finalizePayloadProcessing() {
      if (DoSmallDetailsPart && this.limitation) {
        this.isSmallDetailsOpen = false;
        this.smallDetailsResult = null;

        this.showSmallDetailsLoader = true;
        const productPrintPosition = this.data.productPrintPositions.find(
          ({ name }) => name === this.activePosition.name
        );

        if (!productPrintPosition) return false;

        const selectedTechniqueId = this.unavailablePrintPositions.find(
          ({ positionId }) => positionId === productPrintPosition.id
        )?.technique.id;

        const printTechnique = productPrintPosition.printTechniques.find(
          ({ id }) => id === selectedTechniqueId
        );

        if (!printTechnique) return false;

        for (const logoValidator of printTechnique.logoValidators) {
          if (logoValidator.type.trim() === 'FileType' && this.wizardPayload) {
            const results = fileTypeValidatorSmallDetails(
              logoValidator.configuration,
              this.wizardPayload,
              this.activePosition.name || ''
            );
            this.fileTypeValid = results?.validatorColor === 'Green';
          }
        }
        if (!this.fileTypeValid) {
          this.showSmallDetailsLoader = false;
          this.onProceedWithLogo(undefined);
        } else {
          await this.prepareSmallDetailsResults(
            this.wizardPayload as WizardPayload,
            this.limitation as SmallDetailsLimitations
          );
          this.showSmallDetailsLoader = false;
        }
      } else {
        // TO DO: Remove this flow when small details is finised
        this.wizardPayloadReady(this.wizardPayload as unknown as WizardPayload);
      }
    },

    populateCustomerId() {
      if (this.artworksCollection.page.length === 0) {
        this.customerId = uuidv4();
      } else {
        this.customerId = this.artworksCollection.page[0].customerId;
      }
    },
    onBackToLibrary() {
      this.isSmallDetailsOpen = false;
      this.selectedArtworkId = '';
      if (this.useVersion2) {
        this.$modal.hide('small-details-single-logo-modal');
      } else {
        this.$modal.hide('small-details-one-logo-modal');
      }
    },
    async onUploadReady(payload: ArtworkResult) {
      this.wizardPayload = {
        ...payload,
        pngFileWithoutConverters: payload.pngFile,
        cmykPdfFileUrl: payload.cmykPdfFileUrl ? payload.cmykPdfFileUrl : '',
      } as unknown as WizardPayload;
      await this.processWizardPayload();
    },
    onUploadNewLogo() {
      this.isSmallDetailsOpen = false;
      this.selectedArtworkId = '';
      this.activeDialog = 'UploadArtworkDialog';
    },
    onProceedWithLogo(smallDetailsPayload: SmallDetailsPayload | undefined) {
      if (this.wizardPayload) {
        this.wizardPayload.smallDetailsResult = smallDetailsPayload;
        this.wizardPayloadReady(this.wizardPayload);
      }
    },
    async onRequestLogoImprovement(formData: {
      comment: string;
      improvementOption: string;
      improvementOptionKey: string;
    }) {
      this.isCreatingLogoImprovementRequest = true;

      const productStoreV2 = useProductStoreV2();

      const position = productStoreV2.activePosition;
      const { widthInMM, heightInMM } = this.calculateArtworkSize(
        this.wizardPayload as WizardPayload
      );

      const currentPosition = productStoreV2.printPositions.find(
        (pos) => pos.name === position.name
      );

      const newLogoImprovementForm = {
        customerNumber: this.sapCustomerId,
        productSapCode: productStoreV2.data.name,
        email: this.sapCustomerEmail,
        region: this.region,
        language: this.languageInUse,
        logos: [
          {
            originalFileName: this.wizardPayload?.fileName,
            title: this.wizardPayload?.title,
            printTechniqueSapCode:
              currentPosition?.printTechniques[0].printTechnique.code,
            printPositionSapCode: position.name,
            printSizeWidthMm: widthInMM,
            printSizeHeightMm: heightInMM,
            improvementOption: formData.improvementOption,
            comment: formData.comment,
            improvementOptionKey: formData.improvementOptionKey,
          },
        ],
      } as NewLogoImprovementRequestForm;
      if (this.wizardPayload?.pngFile) {
        const response = await fetch(this.wizardPayload.originalFileUrl);
        const blob = await response.blob();
        let file = new File([blob], this.wizardPayload?.fileName, {
          type: blob.type,
          lastModified: Date.now(),
        });

        await customersAPI
          .submitLogoImprovementRequest([file], newLogoImprovementForm)
          .then(() => {
            this.isCreatingLogoImprovementRequest = false;
            this.$modal.hide('small-details-single-logo-modal');
            this.$modal.show('logo-improvement-request-success-modal');
          });
      } else {
        this.isCreatingLogoImprovementRequest = false;
      }
    },
    doSearch(value: string) {
      this.searchTerm = value;

      // Fixed indexing bug, the value of the page property within Data was never set back to 1
      this.page = 1;
    },

    async dispatchAction(action: string) {
      if (action === 'remove') {
        this.isDeleting = true;
        let result: any;

        result = customersAPI
          .delete(this.selectedArtworkId)
          .catch((error) => {
            this.handleError(error);
            return;
          })
          .then(() => {
            return this.loadTags(this.customerId);
          })
          .then((tagOptions) => {
            this.artworksCollection = {} as PaggedCollection;

            this.tagOptions = tagOptions;

            // TODO: This must be refactored so that the method returns object
            return this.loadArtworks({
              customerId: this.sapCustomerId,
              searchBy: this.searchByCriteria,
              page: 1,
              pageSize: this.pageSize,
            });
          })
          .then(() => {
            this.selectedArtworkId = '';
            this.page = 1;
          })
          .finally(() => (this.isDeleting = false));

        return result;
      }
      if (action === 'edit') {
        this.activeDialog = 'EditArtworkDialog';
        return;
      }

      if (this.env.context === 'wizard' && action === 'import') {
        this.wizardPayload = await this.createPersonalizationArtwork();
        await this.processWizardPayload();
        return;
      }
      if (action === 'cancel') {
        this.selectedArtworkId = '';
        return;
      }

      if (['upload-success', 'edit-success'].includes(action)) {
        this.artworksCollection = {} as PaggedCollection;

        this.tagOptions = await this.loadTags(this.customerId);
        await this.loadArtworks({
          customerId: this.sapCustomerId,
          searchBy: this.searchByCriteria,
          page: 1,
          pageSize: this.pageSize,
        });

        this.selectedArtworkId = '';
        this.activeDialog = '';
      }
    },

    async processWizardPayload() {
      const productStore = this.useVersion2
        ? useProductStoreV2()
        : useProductStore();
      this.limitation = productStore.activePosition.limitation;

      if (
        productStore.isLaserConverter() &&
        this.wizardPayload?.pmsColorsInPngFile.findIndex((c) =>
          c.name.includes('Pure White')
        ) !== -1
      ) {
        this.$modal.show('question-about-white');
      } else if (
        this.isSublimationTechnique &&
        this.wizardPayload?.pmsColorsInPngFile.findIndex((c) =>
          c.name.includes('Pure White')
        ) !== -1
      ) {
        this.$modal.show('question-about-white-replace');
      } else {
        await this.finalizePayloadProcessing();
      }
    },

    async prepareSmallDetailsResults(
      payload: WizardPayload,
      limitation: SmallDetailsLimitations
    ) {
      const { widthInMM } = this.calculateArtworkSize(payload);

      this.smallDetailsResult = await this.smallDetailsCheck(
        payload.id,
        widthInMM,
        limitation.positiveLimitMM,
        limitation.negativeLimitMM,
        payload.pngFile,
        limitation.ignoreColors ||
          Array.from(new Set(payload.pmsColorsInPngFile.map((obj) => obj.name)))
            .length === 1
      );

      this.isSmallDetailsOpen = true;
    },

    async smallDetailsCheck(
      id: string,
      logoWidthInMm: number,
      positiveLimitInMm: number,
      negativeLimitInMm: number,
      file: Blob,
      ignoreColors: boolean
    ): Promise<SmallDetailsResult | null> {
      const command: CheckImageSmallDetailsCommand = {
        id,
        logoWidthInMm,
        positiveLimitInMm,
        negativeLimitInMm,
        ignoreColors,
      };
      return imageProcessingAPI
        .checkImageSmallDetails(command, file)
        .catch((e) => {
          this.handleError(e);
          console.log(e);
          return null;
        })
        .then((resp) => {
          return resp && resp.data;
        })
        .finally(() => (this.showSmallDetailsLoader = false));
    },

    async convertToBlob(fileUrl: string) {
      return axios({
        url: fileUrl,
        method: 'GET',
        responseType: 'blob',
      }).then((response) => {
        return response.data;
      });
    },

    async createPersonalizationArtwork(): Promise<WizardPayload> {
      let payload: WizardPayload = {} as WizardPayload;
      this.isLoading = true;
      try {
        const { data } = await customersAPI.getById(this.selectedArtworkId);

        if (data.engineVersion === ImageProcessingEngineVersion) {
          const pngFileBlob = await this.convertToBlob(data.pngFileUrl);
          const traditionalTechPngFileBlob = data.traditionalTechniquePngFileUrl
            ? await this.convertToBlob(data.traditionalTechniquePngFileUrl)
            : '';

          payload = {
            ...data,
            customerArtworkId: data.id,
            pngFile: pngFileBlob,
            pngFileWithoutConverters: pngFileBlob,
            pmsColorsInPngFile: data.pmsColorsInPngFile,
            personalizationArtworkId: '',
            traditionalTechniquePngFile: traditionalTechPngFileBlob,
          };
          const resp = await customersAPI.createDraftPersonalizationArtwork(
            payload.customerArtworkId
          );
          payload.personalizationArtworkId = resp.data.id;
          payload.id = resp.data.id;
          payload.originalWidthMM = resp.data.originalWidthMM;
          payload.originalHeightMM = resp.data.originalHeightMM;
          payload.originalHorizontalResolutionDPIs =
            resp.data.originalHorizontalResolutionDPIs;
          payload.originalVerticalResolutionDPIs =
            resp.data.originalVerticalResolutionDPIs;
        } else {
          const artwork: Artwork = {
            shouldSaveInLogoLibrary: true,
            shouldSaveDraftForWizard: true,
            title: data.title,
            fileName: data.fileName,
            externalCustomerId: this.sapCustomerId,
            customerId: this.customerId,
            customerArtworkId: data.id,
            currentEngineVersion: ImageProcessingEngineVersion,
          };
          const originalFileBlob = await this.convertToBlob(
            data.originalFileUrl
          );

          const fileForProcessing: VFileUploaderFile = {
            file: new File([originalFileBlob], data.fileName),
            invalidMessage: '',
            invalidMessageTitle: '',
            state: '',
          };

          const response = await imageProcessingAPI.upload(
            artwork,
            fileForProcessing
          );

          payload = {
            ...response.data,
            id: response.data.personalizationArtworkId,
            title: data.title,
            fileName: data.fileName,
            thumbnailUrl: data.thumbnailUrl,
            tags: data.tags,
            pngFileUrl: data.pngFileUrl,
            pngFile: base64StringToBlob(response.data.pngFile, 'image/png'),
            pngFileWithoutConverters: base64StringToBlob(
              response.data.pngFile,
              'image/png'
            ),
            traditionalTechniquePngFileUrl: data.traditionalTechniquePngFileUrl,
            traditionalTechniquePngFile: response.data
              .traditionalTechniquePngFile
              ? base64StringToBlob(
                  response.data.traditionalTechniquePngFile,
                  'image/png'
                )
              : '',
          };
        }
      } catch (e: any) {
        console.log(e);
        this.isLoading = false;
        if (e.response.status === 409) {
          if (
            e.response.data.error.message ===
            'Provided PDF contains more than 10 pages, which is not allowed. Please provide PDF with less than 10 pages.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.mutlipage-pdf-exception'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork. Processing would require too much time. Please provide simplified version.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.timeout-exception'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.image-magick'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.ghost-script'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.resolution-error'
            ).toString();
          } else if (
            e.response.data.error.message ===
            'W2P is unable to process provided artwork due to technical limitations. Please provide different artwork.'
          ) {
            this.errorMessage = this.$t(
              'section.errors.argument-exception'
            ).toString();
          }
          this.isNotificationVisible = true;
        } else {
          this.handleError(e);
          throw e;
        }
      }

      this.isLoading = false;
      return payload;
    },

    wizardPayloadReady(payload: WizardPayload) {
      this.$emit('logo-library:import', payload);
    },

    calculateArtworkSize(payload: WizardPayload) {
      const artworkSpaceConsumed = 97; // value in percents
      let calcArtworkWidth = 0;
      let calcArtworkHeight = 0;
      const CORRECTION_FACTOR = 0;
      // TODO - remove old store after version 1 is no longer used
      const productStore = useProductStore();
      const productStoreV2 = useProductStoreV2();
      const currentProductStore = this.useVersion2
        ? useProductStoreV2()
        : useProductStore();

      const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
        currentProductStore.activePosition;

      const webImageWidthPx =
        originalImageWidthPx &&
        originalImageWebDpi &&
        originalImageDpi &&
        (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
      let rectLineWidth = (lineWidth * (webImageWidthPx as number)) / 100;

      if (this.useVersion2) {
        rectLineWidth = (lineWidthV2 * BACKGROUND_IMAGE_W) / 100;
      }

      const aspectRatioWH = payload.pngWidthPixels / payload.pngHeightPixels;
      const aspectRatioHW = payload.pngHeightPixels / payload.pngWidthPixels;

      const positionId = currentProductStore.activePosition.id;

      if (positionId) {
        const currentPrintPosition = currentProductStore.printPositions?.find(
          (p) => {
            return p.id === positionId;
          }
        );

        var printArea = this.useVersion2
          ? productStoreV2.printPositionArea(positionId, false)
          : productStore.drawPoints(positionId);

        if (
          currentPrintPosition &&
          currentPrintPosition.printArea?.shapeType === 'Rectangle'
        ) {
          const areaRect = {
            x: printArea.x + rectLineWidth / 2,
            y: printArea.y + rectLineWidth / 2,
            w:
              (this.useVersion2 ? printArea.width : printArea.w) -
              rectLineWidth,
            h:
              (this.useVersion2 ? printArea.height : printArea.h) -
              rectLineWidth,
          };

          const artworkRatio = payload.pngWidthPixels / payload.pngHeightPixels;
          const areaRatio = areaRect.w / areaRect.h;

          // Artwork is more landscape than area - fit to width
          if (artworkRatio > areaRatio) {
            calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
            calcArtworkHeight =
              (calcArtworkWidth / payload.pngWidthPixels) *
              payload.pngHeightPixels;
          }
          // Artwork is more portrait than area - fit to height
          else {
            calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
            calcArtworkWidth =
              (calcArtworkHeight / payload.pngHeightPixels) *
              payload.pngWidthPixels;
          }
        }

        if (
          positionId &&
          currentPrintPosition &&
          currentPrintPosition.printArea?.shapeType === 'Ellipse'
        ) {
          const min = printArea.rx < printArea.ry ? printArea.rx : printArea.ry;
          let size = 0;
          if (Math.abs(printArea.ry - printArea.rx) > 630) {
            size = min * 2;
          } else {
            size = min * 1.6;
          }

          if (aspectRatioWH >= aspectRatioHW) {
            calcArtworkWidth = (size * (artworkSpaceConsumed - 15)) / 100;
            calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
          } else {
            calcArtworkHeight = (size * (artworkSpaceConsumed - 15)) / 100;
            calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
          }
        }

        if (
          currentPrintPosition &&
          currentPrintPosition.printArea?.shapeType === 'Polygon'
        ) {
          const size =
            currentProductStore.polygonDetails(positionId).minDistance;

          if (aspectRatioWH >= aspectRatioHW) {
            calcArtworkWidth = (size * artworkSpaceConsumed) / 100;
            calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
          } else {
            calcArtworkHeight = (size * artworkSpaceConsumed) / 100;
            calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
          }
        }
      }

      const dpi = this.useVersion2
        ? BACKGROUND_IMAGE_DPI
        : ((originalImageWebDpi || originalImageDpi) as number);
      return {
        widthInMM: px2mm(calcArtworkWidth, dpi),
        heightInMM: px2mm(calcArtworkHeight, dpi),
      };
    },
    async loadArtworks({
      customerId,
      searchBy,
      page,
      pageSize,
    }: {
      customerId: string;
      searchBy: { title: string; tags: string[] };
      page: number;
      pageSize: number;
    }) {
      this.isLoading = true;
      try {
        const { data } = await customersAPI.getAll({
          customerId,
          searchBy,
          page,
          pageSize,
        });

        /**
         * In case of a frist fetch, collectionPage should default to an empty array
         */
        const { page: collectionPage = [] } = this.artworksCollection;
        const { page: dataPage } = data;

        this.artworksCollection = {
          ...data,
          page: [...collectionPage, ...dataPage],
        };
      } catch (error) {
        this.handleError(error);

        return Promise.resolve([]);
      } finally {
        this.isLoading = false;
      }
    },

    handleError(error: any) {
      if (error && error.response && error.response.status === 400) {
        this.errorMessage = 'notification-inline.user-input-error';
      } else if (error && error.response && error.response.status === 404) {
        this.errorMessage = 'notification-inline.item-not-found';
      } else {
        this.errorMessage = '';
      }

      this.isNotificationVisible = true;
    },

    async loadTags(customerId: string): Promise<string[]> {
      this.tagOptions = [];

      try {
        const { data } = await customersAPI.getTags(customerId);

        return data.map(({ name }) => name);
      } catch (error: unknown) {
        console.log(error);
        this.handleError(error);
        return Promise.resolve([]);
      }
    },

    async onScroll() {
      if (
        this.thumbEl &&
        this.thumbEl.$el.scrollTop + this.thumbEl.$el.clientHeight + 10 >=
          this.thumbEl.$el.scrollHeight
      ) {
        if (this.artworksCollection.hasNextPage) {
          this.page += 1;
        }
      }
    },

    async filterArtworksByTags(selectedTags: string[]) {
      this.artworksCollection = {} as PaggedCollection;
      this.selectedArtworkId = '';

      this.selectedTags = selectedTags;
      await this.loadArtworks({
        customerId: this.sapCustomerId,
        searchBy: this.searchByCriteria,
        page: 1,
        pageSize: this.pageSize,
      });
    },
  },
});
